import React, { useState } from 'react';
import { Container, Typography, Box, Paper, Button, Chip, Accordion, AccordionSummary, AccordionDetails, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VideocamIcon from '@mui/icons-material/Videocam';
import ChecklistIcon from '@mui/icons-material/Checklist';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import WarningIcon from '@mui/icons-material/Warning';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { DESIGN_TOKENS } from '../theme/designTokens';

const GuideSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
}));

const TipCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  border: '1px solid #E5E7EB',
  background: '#FEFEFE',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: '#4338CA',
  },
}));

const GembaGuide = () => {
  const [expanded, setExpanded] = useState<string | false>('what-is-gemba');
  const { t } = useTranslation();
  
  // Set page title for analytics
  usePageTitle('Gemba Guide');

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const recordingTips = t('gembaGuide.bestPractices.tips', { returnObjects: true }) as Record<string, { title: string, content: string }>;
  const mistakesToAvoid = t('gembaGuide.mistakes.items', { returnObjects: true }) as Record<string, { title: string, content: string }>;

  return (
    <GuideSection>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box component="section" role="region" aria-labelledby="gemba-heading">
            <Typography 
              id="gemba-heading"
              variant="h1" 
              component="h1" 
              align="center" 
              gutterBottom 
              sx={{ 
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' }, 
                mb: 3, 
                fontWeight: 700 
              }}
            >
              {t('gembaGuide.title')}
            </Typography>

            <Typography 
              variant="h2" 
              component="h2"
              align="center" 
              paragraph
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                maxWidth: '800px',
                mx: 'auto',
                mb: 6,
                color: '#6B7280',
                lineHeight: 1.6,
              }}
            >
              {t('gembaGuide.subtitle')}
            </Typography>
          </Box>
        </motion.div>

        <Box sx={{ mb: 6 }}>
          <Accordion 
            expanded={expanded === 'what-is-gemba'} 
            onChange={handleChange('what-is-gemba')}
            sx={{ mb: 2, borderRadius: 3, '&:before': { display: 'none' }, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon aria-hidden="true" />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TipsAndUpdatesIcon sx={{ mr: 2, color: '#5E6AD2' }} aria-hidden="true" />
                <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  {t('gembaGuide.whatIs.title')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#F9FAFB', p: 3 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {t('gembaGuide.whatIs.description')}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ mb: 8 }}>
          <Typography variant="h2" component="h2" align="center" sx={{ fontSize: '2rem', fontWeight: 700, mb: 5 }}>
            <VideocamIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} aria-hidden="true" />
            {t('gembaGuide.bestPractices.title')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 3 }}>
            {Object.values(recordingTips).map((tip, index) => (
              <motion.div
                key={tip.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <TipCard>
                  <Chip label={t('gembaGuide.bestPractices.tip', { count: index + 1 })} color="primary" variant="outlined" sx={{ mb: 2, alignSelf: 'flex-start' }} />
                  <Typography variant="h3" component="h3" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 1 }}>{tip.title}</Typography>
                  <Typography variant="body1" sx={{ color: '#6B7280' }}>{tip.content}</Typography>
                </TipCard>
              </motion.div>
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 8 }}>
          <Typography variant="h2" component="h2" align="center" sx={{ fontSize: '2rem', fontWeight: 700, mb: 5 }}>
            <WarningIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem', color: '#D97706' }} aria-hidden="true" />
            {t('gembaGuide.mistakes.title')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {Object.values(mistakesToAvoid).map((mistake, index) => (
              <motion.div
                key={mistake.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Alert severity="warning" sx={{ height: '100%', borderRadius: 2 }}>
                  <Typography variant="h3" component="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 1 }}>{mistake.title}</Typography>
                  <Typography variant="body2">{mistake.content}</Typography>
                </Alert>
              </motion.div>
            ))}
          </Box>
        </Box>

        <Paper 
          sx={{ 
            p: { xs: 3, sm: 6 }, 
            textAlign: 'center',
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(94, 106, 210, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
            border: '1px solid rgba(94, 106, 210, 0.1)',
          }}
        >
          <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '2rem', mb: 3, fontWeight: 700 }}>
            {t('gembaGuide.cta.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7280', maxWidth: '600px', mx: 'auto', mb: 4, lineHeight: 1.6 }}>
            {t('gembaGuide.cta.description')}
          </Typography>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              variant="contained" 
              size="large"
              component={RouterLink}
              to="/upload"
              startIcon={<ChecklistIcon aria-hidden="true" />}
              sx={{ 
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #5E6AD2 0%, #8B5CF6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4C5BC7 0%, #7C3AED 100%)',
                }
              }}
            >
              {t('gembaGuide.cta.button')}
            </Button>
          </motion.div>
        </Paper>
      </Container>
    </GuideSection>
  );
};

export default GembaGuide; 